import React, { createContext, type ReactNode, useCallback, useContext, useMemo } from "react"; 
import { useAuth, useUser } from "@clerk/clerk-react"; 
import type { Role } from "../app-types/index"; 

// 🟢 Uses the compilation replacement safely
export const clerkPublishableKey = (
  import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY || 
  (typeof process !== "undefined" ? process.env?.VITE_CLERK_PUBLISHABLE_KEY : undefined)
) as string | undefined; 

export function isClerkConfigured() { 
  return Boolean(clerkPublishableKey && !clerkPublishableKey.includes("replace_me")); 
} 

interface AuthBridge { 
  configured: boolean; 
  isLoaded: boolean; 
  isSignedIn: boolean; 
  role: Role; 
  user?: ReturnType<typeof useUser>["user"]; 
  getToken: () => Promise<string | null>; 
  signOut: () => Promise<void>; 
} 

const fallbackAuth: AuthBridge = { 
  configured: false, 
  isLoaded: true, 
  isSignedIn: false, 
  role: "client" as Role, 
  getToken: async () => null, 
  signOut: async () => undefined 
}; 

const AuthContext = createContext<AuthBridge>(fallbackAuth); 

export function ClerkBridge({ children }: { children: ReactNode }) { 
  const auth = useAuth(); 
  const user = useUser(); 
  
  const metadataRole = user.user?.publicMetadata?.role; 
  const role: Role = metadataRole === "admin" ? "admin" : ("client" as Role); 
  
  const clerkGetToken = auth.getToken; 
  const clerkSignOut = auth.signOut; 
  
  const getToken = useCallback(() => clerkGetToken(), [clerkGetToken]); 
  
  const signOut = useCallback(async () => { 
    await clerkSignOut(); 
  }, [clerkSignOut]); 

  const value = useMemo<AuthBridge>( 
    () => ({ 
      configured: true, 
      isLoaded: auth.isLoaded, 
      isSignedIn: Boolean(auth.isSignedIn), 
      role, 
      user: user.user, 
      getToken, 
      signOut 
    }), 
    [auth.isLoaded, auth.isSignedIn, getToken, role, signOut, user.user] 
  ); 

  return ( 
    <AuthContext.Provider value={value}> 
      {children} 
    </AuthContext.Provider> 
  ); 
} 

export function useAuthBridge() { 
  return useContext(AuthContext); 
}
